import { Form } from "@/components/ui";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { MatterStatusActionModal } from "./MatterStatusActionModal";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query";
import { useUpdateMatter } from "@/lib/data/react-query/matters/matter";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  getMatterStatusCategoryScope,
  getMatterStatusTransitionActions,
} from "@/lib/data/static/matterStatics";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

type MatterStatusTransitionDropdownProps = {
  matter: Pick<
    Matter,
    "id" | "statusId" | "status" | "attorneyFee" | "dateModified"
  >;
  canTransitionMatterStatus: boolean;
  buttonClassName?: string;
  readOnlyClassName?: string;
};

export function MatterStatusTransitionDropdown({
  matter,
  canTransitionMatterStatus,
  buttonClassName,
  readOnlyClassName,
}: MatterStatusTransitionDropdownProps) {
  const createMatterPhase = useCreateMatterStatusPhase();
  const updateMatterFee = useUpdateMatter({
    successToast: "Attorney fee updated successfully",
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusActionOpen, setStatusActionOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<
    "referred" | "closed" | "reopen" | null
  >(null);
  const isTerminal = ["referred", "closed"].includes(matter.status.categoryId);
  const currentStatusId = matter.statusId;
  const currentStatusLabel = matter.status.label;
  const isTransitionBusy =
    createMatterPhase.isPending || !canTransitionMatterStatus;
  const form = useForm({
    defaultValues: {
      id: currentStatusId,
    },
    mode: "onSubmit",
  });
  const {
    data: matterSettingsData,
    isLoading,
    isError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const matterStatuses = useMemo(() => {
    const statuses = matterSettingsData?.data.statuses ?? [];
    const allowedCategories = getMatterStatusCategoryScope(
      matter.status.categoryId,
    );
    return statuses
      .filter((status) =>
        isTerminal
          ? status.categoryId === matter.status.categoryId &&
            status.id !== currentStatusId
          : status.categoryId !== "referred" &&
            status.categoryId !== "closed" &&
            allowedCategories.includes(
              status.categoryId as (typeof allowedCategories)[number],
            ),
      )
      .sort(
        (a, b) =>
          (a.order ?? Number.MAX_SAFE_INTEGER) -
            (b.order ?? Number.MAX_SAFE_INTEGER) ||
          a.label.localeCompare(b.label),
      )
      .reduce<
        {
          category: string;
          items: { value: string; label: string }[];
        }[]
      >((acc, status) => {
        const category = status.categoryId;
        const item = {
          value: status.id,
          label: status.label,
        };
        const existingCategory = acc.find((c) => c.category === category);
        if (existingCategory) {
          existingCategory.items.push(item);
        } else {
          acc.push({ category, items: [item] });
        }

        return acc;
      }, []);
  }, [
    matter.status.categoryId,
    matterSettingsData,
    isTerminal,
    currentStatusId,
  ]);

  useEffect(() => {
    form.reset({
      id: currentStatusId,
    });
    setStatusDropdownOpen(false);
  }, [currentStatusId, currentStatusLabel, form]);

  const handleStatusChange = async (value: string) => {
    if (value === currentStatusId) {
      setStatusActionOpen(false);
      return;
    }
    const selectedStatus = matterSettingsData?.data.statuses.find(
      (status) => status.id === value,
    );
    setStatusDropdownOpen(false);

    try {
      const res = await createMatterPhase.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId: matter.id,
        statusId: value,
      });
      setStatusActionOpen(false);
      const defaultFee =
        selectedStatus?.defaultAttorneyFee ??
        res.data.defaultAttorneyFee ??
        null;
      const currentFee =
        matter.attorneyFee == null || matter.attorneyFee === ""
          ? null
          : Number(matter.attorneyFee);
      if (defaultFee !== null && currentFee !== Number(defaultFee)) {
        updateMatterFee.mutate({
          attorneyFee: defaultFee,
          dateModified: res.data.dateModified,
          id: matter.id,
        });
      }
    } catch {
      form.setValue("id", currentStatusId, {
        shouldDirty: false,
      });
    }
  };

  if (!currentStatusLabel) {
    return null;
  }

  if (!canTransitionMatterStatus) {
    return (
      <span
        className={cn(
          "bg-light-primary text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold",
          readOnlyClassName,
        )}
      >
        {currentStatusLabel}
      </span>
    );
  }

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <fieldset disabled={isTransitionBusy}>
            <MultiCategorySelectFormField
              placeholder={currentStatusLabel}
              searchPlaceholder="Search statuses..."
              emptyMessage="No status found."
              register={form.register("id")}
              items={matterStatuses}
              selectedLabel={currentStatusLabel}
              actions={getMatterStatusTransitionActions(
                matter.status.categoryId,
              ).map(({ action, label }) => ({
                label,
                onSelect: () => {
                  setStatusAction(action);
                  setStatusActionOpen(true);
                },
                disabled: isTransitionBusy || isLoading || isError,
              }))}
              open={statusDropdownOpen}
              setOpen={setStatusDropdownOpen}
              isItemsError={isError}
              isItemsLoading={isLoading}
              onChange={handleStatusChange}
              deferChangeUntilClosed
              disabled={isTransitionBusy}
              buttonClassName={cn(
                "h-auto min-h-0 w-auto rounded-full border border-transparent bg-light-primary px-2.5 py-0.5 text-xs font-semibold text-primary shadow-none hover:bg-light-primary/80",
                buttonClassName,
              )}
            />
          </fieldset>
        </form>
      </Form>
      {statusAction && (
        <MatterStatusActionModal
          key={statusAction}
          action={statusAction}
          open={statusActionOpen}
          onClosed={() => {
            setStatusAction(null);
          }}
          statuses={(matterSettingsData?.data.statuses ?? [])
            .filter((status) =>
              statusAction === "reopen"
                ? ["pre-litigation", "litigation"].includes(status.categoryId)
                : status.categoryId === statusAction,
            )
            .sort(
              (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                  (b.order ?? Number.MAX_SAFE_INTEGER) ||
                a.label.localeCompare(b.label),
            )}
          busy={isTransitionBusy}
          onClose={() => {
            setStatusActionOpen(false);
          }}
          onSubmit={handleStatusChange}
        />
      )}
    </>
  );
}
